/* cSpell:ignore noneditable checkmarks */
import React from "react";

import { faCircleCheck, faCircleXmark } from "@performyard/fontawesome/pro-solid-svg-icons";
import {
	Checkbox,
	FormField,
	FormInputText,
	FormSubscribe,
	FormTextEditor,
	Icon,
	Text,
	cn,
	usePyForm,
} from "@performyard/py-components";

import type { ReviewFormContainerProps } from "../ReviewFormContainer";
import { ReviewFormContainer } from "../ReviewFormContainer";

export type ExternalReviewFormCardEditableField =
	| "reviewerName"
	| "reviewerEmail"
	| "emailMessage"
	| "makeVisibleWhenFinished"
	| "redactAuthors";

export type ExternalReviewFormCardProps = {
	/**
	 * Whether all fields are view-only
	 * @default false
	 */
	readOnly?: boolean;
	/**
	 * Specific fields that should be editable when readOnly is true
	 * Only applies when readOnly is true - these fields will be editable while others remain view-only
	 * @example ["reviewerName", "reviewerEmail"]
	 */
	editableFields?: Array<ExternalReviewFormCardEditableField>;
	/**
	 * Hides the "Redact authors" checkbox for roles without permission to change it. A form that is
	 * already redacted shows a read-only indicator instead, and its saved value is left untouched.
	 */
	hideRedactAuthorsControl?: boolean;
	/**
	 * Optional CSS classes to apply to the container
	 */
	dangerouslySetClassName?: string;
};

/**
 * ExternalReviewFormCard displays the form fields for an external review form.
 * This should be wrapped in a PYForm component with the externalFormSchema.
 */
export const ExternalReviewFormCard = ({
	readOnly = false,
	editableFields = [],
	hideRedactAuthorsControl = false,
	dangerouslySetClassName,
}: ExternalReviewFormCardProps) => {
	const { id: formId } = usePyForm();

	const isFieldEditable = (fieldName: string) => {
		if (!readOnly) return true; // All fields editable when not in readOnly mode
		return editableFields.includes(fieldName as any); // Field is editable if in editableFields list
	};

	// Readonly view with no editable fields
	if (readOnly && editableFields.length === 0) {
		return (
			<FormSubscribe
				id="external-review-form-card-readonly"
				selector={(state) => ({
					reviewerName: state.values.reviewerName || "",
					reviewerEmail: state.values.reviewerEmail || "",
					emailMessage: state.values.emailMessage || "",
					makeVisibleWhenFinished: state.values.makeVisibleWhenFinished,
					redactAuthors: state.values.redactAuthors,
				})}
			>
				{({ reviewerName, reviewerEmail, emailMessage, makeVisibleWhenFinished, redactAuthors }) => (
					<div className={cn("flex flex-col gap-4", dangerouslySetClassName)}>
						{reviewerName && (
							<div key="reviewer-name" className="grid grid-cols-[180px_1fr] gap-2">
								<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
									{"Reviewer's Name"}
								</Text>
								<Text size="paragraph-3" color="neutral-9">
									{reviewerName}
								</Text>
							</div>
						)}

						{reviewerEmail && (
							<div key="reviewer-email" className="grid grid-cols-[180px_1fr] gap-2">
								<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
									{"Reviewer's Email"}
								</Text>
								<Text size="paragraph-3" color="neutral-9">
									{reviewerEmail}
								</Text>
							</div>
						)}

						{emailMessage && (
							<div key="email-message" className="grid grid-cols-[180px_1fr] gap-2">
								<Text
									size="paragraph-3"
									weight="semi-bold"
									color="neutral-9"
									dangerouslySetClassName="flex items-start pt-1"
								>
									Email Message
								</Text>
								<Text size="paragraph-3" color="neutral-9">
									{emailMessage}
								</Text>
							</div>
						)}

						<div className="flex flex-col gap-2">
							<div className="flex items-center gap-2">
								<Icon
									icon={makeVisibleWhenFinished ? faCircleCheck : faCircleXmark}
									size={20}
									color={makeVisibleWhenFinished ? "green-5" : "red-5"}
								/>
								<Text size="paragraph-3" color="neutral-9">
									Make visible to the subject when form is completed
								</Text>
							</div>
							<div className="flex items-center gap-2">
								<Icon
									icon={redactAuthors ? faCircleCheck : faCircleXmark}
									size={20}
									color={redactAuthors ? "green-5" : "red-5"}
								/>
								<Text size="paragraph-3" color="neutral-9">
									Redact authors
								</Text>
							</div>
						</div>
					</div>
				)}
			</FormSubscribe>
		);
	}

	// Editable/mixed view - form fields auto-connect via id, no wrapper needed
	return (
		<div className={cn("grid grid-cols-[180px_1fr] items-start gap-4", dangerouslySetClassName)}>
			<Text
				size="paragraph-3"
				weight="semi-bold"
				color="neutral-9"
				overrideElement="label"
				htmlFor="reviewerName"
				dangerouslySetClassName="flex h-8 items-center"
			>
				{"Reviewer's Name"}
			</Text>
			<div>
				<FormInputText
					id="reviewerName"
					placeholder="Reviewer's name here"
					disabled={!isFieldEditable("reviewerName")}
					maxLength={1000}
				/>
			</div>

			<Text
				size="paragraph-3"
				weight="semi-bold"
				color="neutral-9"
				overrideElement="label"
				htmlFor="reviewerEmail"
				dangerouslySetClassName="flex h-8 items-center"
			>
				{"Reviewer's Email"}
			</Text>
			<div>
				<FormInputText
					id="reviewerEmail"
					placeholder="Reviewer's email here"
					disabled={!isFieldEditable("reviewerEmail")}
					maxLength={1000}
				/>
			</div>

			<Text
				size="paragraph-3"
				weight="semi-bold"
				color="neutral-9"
				overrideElement="label"
				htmlFor="emailMessage"
				dangerouslySetClassName="flex h-8 items-center"
			>
				Email Message
			</Text>
			<div>
				<FormTextEditor
					id="emailMessage"
					placeholder="Email message"
					disabled={!isFieldEditable("emailMessage")}
				/>
			</div>

			<div className="col-span-2 flex flex-col gap-2">
				<FormField<boolean>
					id="makeVisibleWhenFinished"
					input={(field) => (
						<div className="flex items-center gap-2">
							<Checkbox
								id={`${formId}-makeVisibleWhenFinished`}
								checked={field.value === true}
								onCheckedChange={(checked) => field.onChange(checked)}
								disabled={!isFieldEditable("makeVisibleWhenFinished")}
							/>
							<Text
								size="paragraph-3"
								color="neutral-9"
								overrideElement="label"
								htmlFor={`${formId}-makeVisibleWhenFinished`}
								dangerouslySetClassName="cursor-pointer"
							>
								Make visible to the subject when form is completed
							</Text>
						</div>
					)}
				/>
				<FormField<boolean>
					id="redactAuthors"
					input={(field) =>
						hideRedactAuthorsControl ? (
							field.value === true ? (
								<div className="flex items-center gap-2">
									<Icon icon={faCircleCheck} size={20} color="green-5" />
									<Text size="paragraph-3" color="neutral-9">
										Redact authors
									</Text>
								</div>
							) : null
						) : (
							<div className="flex items-center gap-2">
								<Checkbox
									id={`${formId}-redactAuthors`}
									checked={field.value === true}
									onCheckedChange={(checked) => field.onChange(checked)}
									disabled={!isFieldEditable("redactAuthors")}
								/>
								<Text
									size="paragraph-3"
									color="neutral-9"
									overrideElement="label"
									htmlFor={`${formId}-redactAuthors`}
									dangerouslySetClassName="cursor-pointer"
								>
									Redact authors
								</Text>
							</div>
						)
					}
				/>
			</div>
		</div>
	);
};

ExternalReviewFormCard.displayName = "ExternalReviewFormCard";

/**
 * Combined props for ExternalReviewForm wrapper component
 */
export type ExternalReviewFormProps = Omit<ReviewFormContainerProps, "children"> &
	Omit<ExternalReviewFormCardProps, "dangerouslySetClassName"> & {
		/**
		 * Optional CSS classes to apply to the outer container
		 */
		containerClassName?: string;
		/**
		 * Optional CSS classes to apply to the inner card
		 */
		cardClassName?: string;
	};

/**
 * ExternalReviewForm combines ReviewFormContainer and ExternalReviewFormCard
 * into a single component for easier usage.
 *
 * This is the recommended component to use. It includes the container with
 * header, delete button, and template icon functionality.
 */
export const ExternalReviewForm = ({
	// Container props
	name,
	fromTemplate,
	onDelete,
	containerClassName,
	"data-testid": dataTestId,
	// Card props
	readOnly,
	editableFields,
	hideRedactAuthorsControl,
	cardClassName,
}: ExternalReviewFormProps) => (
	<ReviewFormContainer
		name={name}
		fromTemplate={fromTemplate}
		onDelete={onDelete}
		dangerouslySetClassName={containerClassName}
		data-testid={dataTestId}
	>
		<ExternalReviewFormCard
			readOnly={readOnly}
			editableFields={editableFields}
			hideRedactAuthorsControl={hideRedactAuthorsControl}
			dangerouslySetClassName={cardClassName}
		/>
	</ReviewFormContainer>
);

ExternalReviewForm.displayName = "ExternalReviewForm";
